import { inject, Injectable } from '@angular/core';
import {
  AuthService,
  IsophiCoreService,
  IStorage,
  LicenseService as CoreLicenseService,
  LocalStorage,
  LinksService,
} from '@isophi/core-legacy';
import { environment } from '../../../environments/environment';
import { catchError, from, Observable, of, map, mergeMap, take } from 'rxjs';
import { KindergartenService } from '@app/core/services/kindergarten.service';
import { EXTERNAL_LOGIN_NOTICE_KEY } from '../constants/constants';
import { Router } from '@angular/router';
import { RouterLinkFactory } from '../router/router-link.factory';
import { PopupService } from '@app/shared/services/popup.service';
import { Location } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class InitService {
  private readonly router = inject(Router);
  private readonly links = inject(RouterLinkFactory);
  private readonly popupService = inject(PopupService);
  private readonly linksService = inject(LinksService);
  private readonly location = inject(Location);

  protected storage: IStorage;

  public constructor(
    private isophiCoreService: IsophiCoreService,
    private authService: AuthService,
    private coreLicenseService: CoreLicenseService,
    private kindergartenService: KindergartenService
  ) {
    this.storage = new LocalStorage();
  }

  /**
   * Init application.
   */
  public initApplication(): Observable<void> {
    // init @isophi/core-legacy library
    this.isophiCoreService.setUpRequiredFields(
      environment.common.version,
      environment.common.appKeyword,
      environment.client_id,
      environment.parentApiBase + '/api/v1'
    );
    this.isophiCoreService.setField('auth-api', environment.authAPI);
    this.isophiCoreService.setField('logged-teacher-api', environment.teacherAPI);
    this.isophiCoreService.setField('license-api', environment.licenseAPI);

    this.linksService.setConfig({
      dadAppUrl: '',
      managementAppUrl: environment.managementApplicationUrl,
      papAppUrl: '',
      teacherAppUrl: environment.teacherAppUrl,
    });

    // restore access token and try to load user
    this.authService.restoreToken(this.storage);
    return this.authByAccessToken().pipe(
      take(1),
      map(() => void 0),
      catchError((err) => {
        console.error('Init failed:', err);
        return of(void 0);
      })
    );
  }

  authByAccessToken(externalLogin = false): Observable<{ innerReroute: boolean }> {
    return from(this.authService.authenticateByAccessToken()).pipe(
      mergeMap(() => from(this.kindergartenService.loadLoggedKindergarten())),
      map(() => {
        let innerReroute = false;
        const loggedKindergarten = this.kindergartenService.loggedKindergarten;
        if (loggedKindergarten?.uuid) {
          this.coreLicenseService.licenseSubjectUuid = loggedKindergarten.uuid;
        } else {
          this.coreLicenseService.licenseSubjectUuid = '';
        }

        /**
         * Make redirects/show popups only if user is not trying to login throught external token.
         * Because innitialy we have old user and that can mess up logic below (we need to wait for new users profile)
         */
        if (!this.location.path().includes('login/external') || externalLogin) {
          this.popupService.tryShowPopup('pap_app_initial').subscribe((shown) => {
            const userExternalLoginKey = `${EXTERNAL_LOGIN_NOTICE_KEY}${this.authService.loggedUser.uuid}`;

            if (loggedKindergarten && !this.storage.getItem(userExternalLoginKey) && !loggedKindergarten.isDemo) {
              innerReroute = true;

              if (shown) {
                this.popupService.modalClosed$.pipe(take(1)).subscribe(() => {
                  this.routeToExternalLoginConfirmation(userExternalLoginKey);
                });
              } else {
                this.routeToExternalLoginConfirmation(userExternalLoginKey);
              }
            }
          });
        }

        return {
          innerReroute,
        };
      })
    );
  }

  private routeToExternalLoginConfirmation(userExternalLoginKey: string) {
    this.storage.setItem(userExternalLoginKey, 'true', false);
    this.router.navigate(this.links.externalLoginConfirmation());
  }
}
